"use client";

import type {
  ClinicalProviderFormData,
  ClinicalProviderSearchResult,
} from "@/app/directories/checkout/components/intake/IntakeClinicalProviderInput";
import type { ClinicalProviderResult as DirectClinicalProviderResult } from "@/app/directories/companion/components_v2/PatientHeader/UI/ClinicalProviderWidget";
import { searchForClinicalProvider as directSearchForClinicalProvider } from "@frontend/lib/actions/actions";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import {
  AccountProfileKeys,
  IntakeFormStatesKeys,
  TypeformIntakeKeys,
} from "@frontend/lib/translation-types";
import { cn } from "@frontend/shared/lib/utils";
import { Loader2, Pencil, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { UseFormSetError } from "react-hook-form";
import { useKiosk } from "../(render)/kiosk-provider";
import { KioskButton } from "./KioskButton";
import { KioskInput, KioskNameInput } from "./KioskInput";
import { KioskLabel } from "./KioskLabel";

/**
 * Highlights the matching portion of a name based on the search value.
 * Returns a React fragment with the matched portion wrapped in a teal-colored span.
 * Case-insensitive match.
 */
const highlightSearchMatch = (
  name: string,
  searchValue: string,
): React.ReactNode => {
  if (!searchValue) return name;
  const lowerName = name.toLowerCase();
  const lowerSearch = searchValue.toLowerCase();
  const matchIndex = lowerName.indexOf(lowerSearch);
  if (matchIndex === -1) return name;

  const before = name.slice(0, matchIndex);
  const match = name.slice(matchIndex, matchIndex + searchValue.length);
  const after = name.slice(matchIndex + searchValue.length);

  return (
    <>
      {before}
      <span className="text-kioskSecondary">{match}</span>
      {after}
    </>
  );
};

/**
 * True when manually entered details carry enough content to count as a
 * captured provider: a name, a first + last pair, a phone, or a full address.
 */
const hasValidManualEntry = (formData: ClinicalProviderFormData): boolean => {
  const trimmed = {
    name: formData.name?.trim(),
    firstName: formData.firstName?.trim(),
    lastName: formData.lastName?.trim(),
    phone: formData.phone?.trim(),
    streetAddress: formData.address.streetAddress?.trim(),
    city: formData.address.city?.trim(),
    state: formData.address.state?.trim(),
    zip: formData.address.zip?.trim(),
  };

  return !!(
    trimmed.name ||
    (trimmed.firstName && trimmed.lastName) ||
    trimmed.phone ||
    (trimmed.streetAddress && trimmed.city && trimmed.state && trimmed.zip)
  );
};

/**
 * True when the form holds a provider the parent will actually receive: a
 * selected `ehrId`, or a manual entry complete enough to save — `lastName` plus
 * a 5-character ZIP in `pcp` mode, `name` plus a 5-character ZIP in
 * `pharmacy` mode. Single source of truth for the `onChange` gate and the PCP
 * footer label, so the button can never offer to save data the parent will not
 * be handed.
 */
const hasCompleteProvider = (
  formData: ClinicalProviderFormData,
  mode: "pcp" | "pharmacy",
): boolean => {
  if (formData.ehrId && formData.ehrId.toString() !== "") return true;
  const requiredName = mode === "pcp" ? formData.lastName : formData.name;
  return !!requiredName && formData.address.zip?.length === 5;
};

/** Extracts the 5-digit ZIP prefix from a potentially hyphenated ZIP+4 value. */
const extractZipFromValue = (value: string | undefined): string =>
  value?.split("-")[0]?.slice(0, 5) || "";

// Helper function to map DirectClinicalProviderResult to ClinicalProviderSearchResult
const mapDirectResultToSearchResult = (
  result: DirectClinicalProviderResult,
): ClinicalProviderSearchResult => {
  return {
    streetAddress: result.address,
    city: result.city,
    state: result.state,
    zip: result.zip,
    name: result.name,
    phone: result.phone,
    firstName: result.firstname,
    lastName: result.lastname,
    ehrId: result.clinicalproviderid
      ? Number(result.clinicalproviderid)
      : undefined,
    ncpdpId: result.ncpdpid,
  };
};

interface KioskClinicalProviderInputProps {
  value?: string | ClinicalProviderFormData | null;
  onChange?: (value: ClinicalProviderFormData | null) => void;
  // biome-ignore lint/suspicious/noExplicitAny: Control type is generic
  setError?: UseFormSetError<any>;
  clearErrors?: () => void;
  onBlur?: () => void;
  name?: string;
  id?: string;
  error?: boolean;
  mode?: "pcp" | "pharmacy";
  onContinueDisableChange?: (disabled: boolean) => void;
  onContinueButtonTextChange?: (text: string) => void;
}

const searchExperityPCPs = async (
  searchTerm: string,
  searchZip: string,
  limit = 100,
  radius = 200,
) => {
  const response = await fetch(
    `/api/intake/search-experity-pcp?name=${encodeURIComponent(
      searchTerm,
    )}&zip=${encodeURIComponent(searchZip)}&limit=${limit}&radius=${radius}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        accept: "*/*",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to search: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

export const KioskClinicalProviderInput = ({
  value,
  onChange,
  onBlur,
  name,
  id,
  error,
  setError,
  clearErrors,
  mode = "pharmacy",
  onContinueDisableChange,
  onContinueButtonTextChange,
}: KioskClinicalProviderInputProps) => {
  const orgIdForSearch = 1; // hardcoded to Sorin for now since the only thing this is for is the Athena search endpoint
  const showManualAfterPages = 2; // Number of pages to show before allowing manual entry

  const { state } = useKiosk();

  const intakeAddressQuestion = state.context.intakeFormSections
    // biome-ignore lint/suspicious/noExplicitAny: FIXME: i'm sorry
    ?.flatMap((section: any) => section.questions)
    .find(
      // biome-ignore lint/suspicious/noExplicitAny: FIXME: i'm sorry
      (question: any) =>
        question.intakeQuestion.superscriptIntakeQuestion?.id === 17,
    )?.intakeQuestion.id;

  const intakeAddressQuestionValue = (state.context.responses?.[
    intakeAddressQuestion
  ] ?? state.context.responses?.[String(intakeAddressQuestion)]) as
    | string
    | undefined;

  // Local state
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState<
    ClinicalProviderSearchResult[]
  >([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchFinished, setSearchFinished] = useState(false);
  const [searchZip, setSearchZip] = useState(
    extractZipFromValue(intakeAddressQuestionValue) ||
      extractZipFromValue(state.context.zip as string | undefined) ||
      "",
  );
  const [searchPage, setSearchPage] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [zipError, setZipError] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownScrollRef = useRef<HTMLDivElement>(null);
  const searchGroupRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  const t_typeform = useTranslations("TypeformIntake");
  const t_account = useTranslations("AccountProfile");
  const t = useTranslations("Kiosk.MedriteCheckin");

  // Sync searchZip from intake address when it becomes available after mount.
  // Intake address takes precedence over context.zip fallback (e.g. patient moved).
  const hasUserEditedZip = useRef(false);
  useEffect(() => {
    if (!hasUserEditedZip.current && intakeAddressQuestionValue) {
      const zip = extractZipFromValue(intakeAddressQuestionValue);
      if (zip && zip !== searchZip) setSearchZip(zip);
    }
  }, [intakeAddressQuestionValue, searchZip]);

  // Initialize form data from value, or from existing consumer data in context
  // biome-ignore lint/correctness/useExhaustiveDependencies: exclude state.context — only recompute on value/mode change
  const initialFormData = useMemo((): ClinicalProviderFormData => {
    if (value) {
      try {
        return typeof value === "string" ? JSON.parse(value) : value;
      } catch {
        // Fall through to context-based prepopulation
      }
    }

    // No form value — prepopulate from existing consumer data on parent context
    const ctx = state.context;
    const str = (v: unknown) => (typeof v === "string" ? v : "") as string;
    // ehrId may be numeric or UUID string — keep as-is for hasSelectedValue check
    const id = (v: unknown) => (v || undefined) as number | undefined;

    if (mode === "pharmacy" && ctx.pharmacyName) {
      return {
        name: str(ctx.pharmacyName),
        phone: str(ctx.pharmacyPhone),
        ehrId: id(ctx.pharmacyEhrId),
        ncpdpId: id(ctx.pharmacyNcpdpId),
        address: {
          streetAddress: str(ctx.pharmacyStreetAddress),
          city: str(ctx.pharmacyCity),
          state: str(ctx.pharmacyState),
          zip: str(ctx.pharmacyZip),
        },
      };
    }

    if (mode === "pcp" && (ctx.pcpFirstName || ctx.pcpLastName)) {
      const firstName = str(ctx.pcpFirstName);
      const lastName = str(ctx.pcpLastName);
      return {
        name: `${firstName} ${lastName}`.trim(),
        firstName,
        lastName,
        phone: str(ctx.pcpPhone),
        ehrId: id(ctx.pcpEhrId),
        address: {
          streetAddress: str(ctx.pcpAddress),
          city: str(ctx.pcpCity),
          state: str(ctx.pcpState),
          zip: str(ctx.pcpZip),
        },
      };
    }

    return {
      name: "",
      address: { streetAddress: "", city: "", state: "", zip: "" },
      phone: "",
    };
  }, [value, mode]);

  const [formData, setFormData] =
    useState<ClinicalProviderFormData>(initialFormData);

  // PCP mode: the footer button follows one rule — will the parent actually be
  // handed a provider? This is the same `hasCompleteProvider` predicate that
  // gates `onChange` below, so the label cannot disagree with the submitted
  // value.
  // - Nothing saveable (no selection, incomplete manual entry): "Skip".
  //   Leftover text in the search box does not count; it never becomes the
  //   form value.
  // - Provider selected, or manual entry complete enough to save: "Next".
  // Either way continue stays enabled — PCP is optional, so the footer never
  // blocks the patient.
  const hasCapturedProvider = hasCompleteProvider(formData, mode);

  useEffect(() => {
    if (mode === "pcp" && onContinueButtonTextChange) {
      onContinueButtonTextChange(hasCapturedProvider ? t("Next") : t("Skip"));
    }
  }, [hasCapturedProvider, mode, t, onContinueButtonTextChange]);

  useEffect(() => {
    if (mode === "pcp" && onContinueDisableChange) {
      onContinueDisableChange(false);
    }
  }, [mode, onContinueDisableChange]);

  // Update parent when formData changes. Gated by the same
  // `hasCompleteProvider` predicate that drives the PCP footer label above —
  // an incomplete entry sends `null`, so "Skip" and "no value saved" always
  // agree.
  useEffect(() => {
    if (onChange) {
      onChange(hasCompleteProvider(formData, mode) ? formData : null);
    }
  }, [formData, onChange, mode]);

  // Search handler for button-search mode
  const searchItemsButtonMode = useCallback(
    async (searchTerm: string, searchZip: string, page: number) => {
      if (searchZip?.length !== 5 || !orgIdForSearch) {
        if (page === 0) {
          setShowDropdown(false);
          setSearchResults([]);
        }
        return;
      }
      setSearchFinished(false);

      try {
        setIsSearching(true);

        // Experity has no pagination — return all matches in one shot.
        if (mode === "pcp") {
          if (page !== 0) return;
          const results = await searchExperityPCPs(searchTerm, searchZip);
          setSearchResults(results);
          setHasMoreResults(false);
          setSearchFinished(true);
          return;
        }

        // Pharmacy (Athena) paginates — probe page+1 to detect more results.
        const pharmacySearchTerm = searchTerm.trim() || "pharmacy";
        const currentPageResults = await directSearchForClinicalProvider(
          pharmacySearchTerm,
          "",
          searchZip,
          orgIdForSearch,
          page,
        );
        const nextPageResults = await directSearchForClinicalProvider(
          pharmacySearchTerm,
          "",
          searchZip,
          orgIdForSearch,
          page + 1,
        );

        const mappedResults = currentPageResults.map(
          mapDirectResultToSearchResult,
        );

        if (page === 0) {
          setSearchResults(mappedResults);
        } else {
          // Store scroll position before appending
          const container = dropdownScrollRef.current;
          if (container) {
            scrollPositionRef.current = container.scrollTop;
          }
          setSearchResults((prev) => [...prev, ...mappedResults]);

          // Restore scroll position after DOM update
          setTimeout(() => {
            if (container && scrollPositionRef.current > 0) {
              container.scrollTop = scrollPositionRef.current;
            }
          }, 0);
        }

        setHasMoreResults(nextPageResults.length > 0);
        setSearchFinished(true);
      } catch (error) {
        console.error(
          "[KioskClinicalProviderInput] Error fetching clinical provider search results:",
          error,
        );
        setSearchResults([]);
        setHasMoreResults(false);
        setSearchFinished(true);
      } finally {
        setIsSearching(false);
      }
    },
    [mode],
  );

  // Handle button search click
  const handleButtonSearch = () => {
    if (searchZip?.length !== 5) {
      setZipError(true);
      if (id) {
        setError?.(id, { message: IntakeFormStatesKeys.Required });
      }
      return;
    }
    setZipError(false);
    setSearchPage(0);
    setSearchResults([]);
    searchItemsButtonMode(searchValue, searchZip, 0);
    setShowDropdown(true);
  };

  // Handle infinite scroll - load more when near bottom
  const handleScroll = useCallback(() => {
    const container = dropdownScrollRef.current;
    if (!container || !hasMoreResults || isSearching) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 100; // pixels from bottom

    if (scrollHeight - scrollTop - clientHeight < threshold) {
      const nextPage = searchPage + 1;
      setSearchPage(nextPage);
      searchItemsButtonMode(searchValue, searchZip, nextPage);
    }
  }, [
    hasMoreResults,
    isSearching,
    searchPage,
    searchValue,
    searchZip,
    searchItemsButtonMode,
  ]);

  // Set up scroll listener for infinite scroll
  useEffect(() => {
    const container = dropdownScrollRef.current;
    if (!container || !showDropdown) {
      return;
    }

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, showDropdown]);

  // Lock body scroll, handle Escape key, and close on click outside
  useEffect(() => {
    if (showDropdown) {
      // Prevent body scroll
      document.body.style.overflow = "hidden";

      // Handle Escape key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          setShowDropdown(false);
        }
      };

      // Handle click outside dropdown
      const handleClickOutside = (e: MouseEvent | TouchEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(e.target as Node)
        ) {
          setShowDropdown(false);
        }
      };

      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);

      return () => {
        document.body.style.overflow = "unset";
        document.removeEventListener("keydown", handleEscape);
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
      };
    }
  }, [showDropdown]);

  // Handle selecting a search result
  const handleSelectResult = (result: ClinicalProviderSearchResult) => {
    const newFormData: ClinicalProviderFormData = {
      name: result.name || "",
      lastName: result.lastName,
      firstName: result.firstName,
      address: {
        streetAddress: result.streetAddress,
        city: result.city,
        state: result.state,
        zip: result.zip,
      },
      phone: result.phone ?? "",
      ehrId: result.ehrId ? result.ehrId : undefined,
      ncpdpId: result.ncpdpId ? result.ncpdpId : undefined,
    };
    setFormData(newFormData);
    setSearchValue("");
    setShowDropdown(false);

    // Provider selected, enable continue
    onContinueDisableChange?.(false);
  };

  // Handle manual entry form changes
  const handleFormChange = (
    field: keyof ClinicalProviderFormData,
    inputValue: string | number | undefined,
  ) => {
    const value = inputValue || "";
    let newFormData: ClinicalProviderFormData;

    if (field === "firstName") {
      newFormData = {
        ...formData,
        firstName: value ? value.toString() : "",
        name: `${value} ${formData.lastName || ""}`,
      };
    } else if (field === "lastName") {
      newFormData = {
        ...formData,
        lastName: value ? value.toString() : "",
        name: `${formData.firstName || ""} ${value}`,
      };
    } else {
      newFormData = {
        ...formData,
        [field]: value,
      };
    }

    setFormData(newFormData);
    onContinueDisableChange?.(
      computeContinueDisabled(newFormData, isManualEntry),
    );
  };

  const handleAddressChange = (
    field: keyof ClinicalProviderFormData["address"],
    value: string,
  ) => {
    const newFormData = {
      ...formData,
      address: {
        ...formData.address,
        [field]: value,
      },
    };
    setFormData(newFormData);
    onContinueDisableChange?.(
      computeContinueDisabled(newFormData, isManualEntry),
    );
  };

  const handleReset = () => {
    const emptyFormData: ClinicalProviderFormData = {
      name: "",
      address: {
        streetAddress: "",
        city: "",
        state: "",
        zip: "",
      },
      phone: "",
      ehrId: undefined,
      ncpdpId: undefined,
    };
    setFormData(emptyFormData);
    setSearchValue("");
    setSearchPage(0);
    setHasMoreResults(false);

    // Reset clears selection, disable continue (back to search mode)
    onContinueDisableChange?.(
      computeContinueDisabled(emptyFormData, isManualEntry),
    );
  };

  // If we have a selected value, show it with options to edit
  const hasSelectedValue = useMemo(() => {
    return (
      formData.ehrId !== undefined &&
      formData.ehrId !== null &&
      formData.ehrId.toString() !== ""
    );
  }, [formData]);

  // Helper to compute if continue should be disabled
  const computeContinueDisabled = useCallback(
    (
      currentFormData: ClinicalProviderFormData,
      currentIsManualEntry: boolean,
    ): boolean => {
      // PCP is optional — continue is never blocked. It reads "Skip" until a
      // provider is captured, then "Next".
      if (mode === "pcp") {
        return false;
      }
      // If provider is selected (has ehrId), enable continue
      const hasProvider =
        currentFormData.ehrId !== undefined &&
        currentFormData.ehrId !== null &&
        currentFormData.ehrId.toString() !== "";
      if (hasProvider) return false;

      // If not in manual entry mode and no provider, disable
      if (!currentIsManualEntry) return true;

      // In manual entry: check if any required field has valid content
      return !hasValidManualEntry(currentFormData);
    },
    [mode],
  );

  // Set initial disabled state on mount only
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!hasInitializedRef.current && onContinueDisableChange) {
      hasInitializedRef.current = true;
      onContinueDisableChange(computeContinueDisabled(formData, isManualEntry));
    }
  }, [
    onContinueDisableChange,
    formData,
    isManualEntry,
    computeContinueDisabled,
  ]);

  return (
    <div className="w-full flex flex-col gap-3 relative">
      {/* Search / zip section: shown when no provider selected yet */}
      {!hasSelectedValue && (
        <div className="flex flex-row gap-2 items-center justify-between">
          {!isManualEntry ? (
            <div
              className="flex flex-col gap-1 w-full relative"
              ref={dropdownRef}
            >
              {mode === "pcp" ? (
                <>
                  {/* PCP mode: full-width search */}
                  <div className="flex flex-col gap-1" ref={searchGroupRef}>
                    <p className="text-base font-medium tracking-[-0.32px]">
                      {t("SearchByNameLabel")}
                    </p>
                    <div
                      className={cn(
                        "flex flex-row gap-2 items-center border rounded-full h-16 px-3 bg-white overflow-hidden",
                        "border-[#e1e1e1]",
                        error && "border-red-500",
                      )}
                    >
                      <input
                        type="text"
                        aria-label="PCP search"
                        className="flex-grow min-w-0 bg-transparent border-none outline-none text-base h-full placeholder:text-gray-500"
                        placeholder={t_typeform(
                          TypeformIntakeKeys.PCPSearchPlaceholder,
                        )}
                        value={searchValue}
                        onChange={(e) => {
                          clearErrors?.();
                          setSearchFinished(false);
                          setSearchValue(e.target.value);
                          setSearchPage(0);
                          setSearchResults([]);
                          setHasMoreResults(false);
                          if (!e.target.value) {
                            setShowDropdown(false);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && searchZip?.length === 5) {
                            e.preventDefault();
                            handleButtonSearch();
                          }
                        }}
                        id={id}
                        name={name}
                        onBlur={() => {
                          onBlur?.();
                        }}
                        data-testid="search-input"
                      />
                      <KioskButton
                        type="button"
                        onClick={handleButtonSearch}
                        disabled={
                          searchZip?.length !== 5 ||
                          isSearching ||
                          !orgIdForSearch
                        }
                        isLoading={isSearching}
                        size="md"
                        className="flex rounded-full bg-kioskPrimary text-white px-6 py-3 min-h-0 gap-2.5 shrink-0"
                        fullWidth={false}
                        data-testid="search-button"
                      >
                        {!isSearching && (
                          <span>{t_typeform(TypeformIntakeKeys.Search)}</span>
                        )}
                        {!isSearching && <Search className="h-4 w-4" />}
                      </KioskButton>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Pharmacy mode: side-by-side ZIP + search (stacked on mobile) */}
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* ZIP group */}
                    <div className="flex flex-col gap-1 md:w-[158px] md:shrink-0">
                      <p className="text-base font-medium tracking-[-0.32px]">
                        {t("PharmacyZipCodeLabel")}
                      </p>
                      <input
                        type="text"
                        value={searchZip}
                        data-testid="zip-input"
                        onChange={(e) => {
                          setSearchFinished(false);
                          setZipError(false);
                          const newZip = e.target.value;
                          if (!/^\d*$/.test(newZip) || newZip?.length > 5) {
                            return;
                          }
                          hasUserEditedZip.current = true;
                          setSearchZip(newZip);
                          setSearchPage(0);
                          setSearchResults([]);
                          setHasMoreResults(false);
                        }}
                        maxLength={5}
                        placeholder=""
                        className={cn(
                          "w-full h-16 rounded-full border px-4 text-base md:text-lg bg-white",
                          "border-[#e1e1e1]",
                          zipError
                            ? "border-red-600"
                            : "focus:border-kioskPrimary focus:outline-none focus:ring-0",
                        )}
                      />
                    </div>
                    {/* Search group */}
                    <div
                      className="flex flex-col gap-1 flex-1"
                      ref={searchGroupRef}
                    >
                      <p className="text-base font-medium tracking-[-0.32px]">
                        {t("SearchByNameLabel")}
                      </p>
                      <div
                        className={cn(
                          "flex flex-1 h-16 pl-4 pr-2 py-2 justify-between items-center border rounded-full bg-white overflow-hidden",
                          "border-[#e1e1e1]",
                          error && "border-red-500",
                        )}
                      >
                        <input
                          type="text"
                          aria-label="Pharmacy search"
                          className="flex-grow min-w-0 bg-transparent border-none outline-none text-base md:text-lg h-full placeholder:text-gray-500"
                          placeholder={t_typeform(
                            TypeformIntakeKeys.SearchForYourPharmacyPlaceholder,
                          )}
                          value={searchValue}
                          onChange={(e) => {
                            clearErrors?.();
                            setSearchFinished(false);
                            setSearchValue(e.target.value);
                            setSearchPage(0);
                            setSearchResults([]);
                            setHasMoreResults(false);
                            if (!e.target.value) {
                              setShowDropdown(false);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && searchZip?.length === 5) {
                              e.preventDefault();
                              handleButtonSearch();
                            }
                          }}
                          id={id}
                          name={name}
                          onBlur={onBlur}
                          data-testid="search-input"
                        />
                        <KioskButton
                          type="button"
                          onClick={handleButtonSearch}
                          disabled={
                            searchZip?.length !== 5 ||
                            isSearching ||
                            !orgIdForSearch
                          }
                          isLoading={isSearching}
                          size="md"
                          className="flex rounded-full bg-kioskPrimary text-white px-6 py-3 min-h-0 gap-2.5 shrink-0"
                          fullWidth={false}
                          data-testid="search-button"
                        >
                          {!isSearching && (
                            <span>{t_typeform(TypeformIntakeKeys.Search)}</span>
                          )}
                          {!isSearching && <Search className="h-4 w-4" />}
                        </KioskButton>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {/* Zip validation message */}
              {zipError && (
                <p className="text-xs text-red-600" data-testid="zip-error">
                  {t_typeform(TypeformIntakeKeys.EnterZip)}
                </p>
              )}
              {/* Results dropdown — rendered inside the inputs container so absolute positioning aligns with input width */}
              {showDropdown && searchZip?.length === 5 && (
                <>
                  {/* Mobile Modal */}
                  <div className="md:hidden">
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 bg-black bg-opacity-50 z-40"
                      onClick={() => setShowDropdown(false)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setShowDropdown(false);
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label="Close search results"
                      data-testid="search-modal-backdrop"
                    />

                    {/* Modal Content - Centered Card */}
                    <div
                      className="fixed inset-0 z-50 flex items-center justify-center p-4"
                      onClick={(e) => {
                        // Close if clicking the container (not the modal content)
                        if (e.target === e.currentTarget) {
                          setShowDropdown(false);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Escape" || e.key === "Enter") {
                          setShowDropdown(false);
                        }
                      }}
                      aria-modal="true"
                    >
                      <div
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
                        data-testid="search-results-dropdown"
                      >
                        {/* Modal Body */}
                        {isSearching && searchResults.length === 0 ? (
                          <div className="flex items-center justify-center p-8">
                            <Loader2 className="animate-spin" size={32} />
                          </div>
                        ) : searchResults?.length > 0 ? (
                          <>
                            {/* Scrollable results area */}
                            <div
                              ref={dropdownScrollRef}
                              className="max-h-[234px] overflow-y-auto"
                            >
                              {searchResults.map((result, index) => (
                                <button
                                  key={`${result.ehrId}-${index}`}
                                  type="button"
                                  data-pharmacy-result
                                  data-testid={`search-result-${index}-mobile`}
                                  className="w-full px-6 py-4 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-all duration-200 border-b border-gray-200 first:rounded-t-3xl"
                                  onClick={() => {
                                    handleSelectResult(result);
                                    setShowDropdown(false);
                                  }}
                                >
                                  <div className="font-medium text-base">
                                    {result.name}
                                  </div>
                                  <div className="text-sm text-gray-500 mt-1">
                                    {result.streetAddress}, {result.city},{" "}
                                    {result.state} {result.zip}
                                  </div>
                                </button>
                              ))}
                              {/* Loading more indicator */}
                              {isSearching && searchResults.length > 0 && (
                                <div className="flex items-center justify-center p-4 border-t border-gray-200">
                                  <Loader2 className="animate-spin" size={20} />
                                  <span className="ml-2 text-sm text-gray-500">
                                    {t_typeform(
                                      TypeformIntakeKeys.LoadingMoreResults,
                                    )}
                                  </span>
                                </div>
                              )}
                            </div>
                            {/* Sticky footer with "Enter Manually" button */}
                            {(searchPage >= showManualAfterPages ||
                              !hasMoreResults) && (
                              <div className="border-t border-gray-200 p-6 bg-white rounded-b-3xl sticky bottom-0 flex flex-col gap-3 items-center justify-center">
                                <p className="text-sm text-gray-500 text-center">
                                  {mode === "pcp"
                                    ? t_typeform(
                                        TypeformIntakeKeys.NotSeeingYourPCP,
                                      )
                                    : t_typeform(
                                        TypeformIntakeKeys.NotSeeingYourPharmacy,
                                      )}
                                </p>
                                <KioskButton
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="flex items-center px-6"
                                  onClick={() => {
                                    setIsManualEntry(true);
                                    setShowDropdown(false);
                                    onContinueDisableChange?.(
                                      computeContinueDisabled(formData, true),
                                    );
                                  }}
                                  fullWidth={false}
                                  data-testid="enter-manually-button-mobile"
                                >
                                  <Pencil className="h-4 w-4 min-w-4" />
                                  {t_typeform(TypeformIntakeKeys.EnterManually)}
                                </KioskButton>
                              </div>
                            )}
                          </>
                        ) : (
                          searchFinished && (
                            <div className="flex flex-col items-center justify-center p-8 gap-4">
                              <p className="text-base text-gray-500 text-center">
                                {t_typeform(TypeformIntakeKeys.NoResultsFound)}
                              </p>
                              <KioskButton
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex items-center"
                                onClick={() => {
                                  setIsManualEntry(true);
                                  setShowDropdown(false);
                                  onContinueDisableChange?.(
                                    computeContinueDisabled(formData, true),
                                  );
                                }}
                                fullWidth={false}
                                data-testid="enter-manually-button-mobile"
                              >
                                <Pencil className="h-4 w-4" />
                                {t_typeform(
                                  TypeformIntakeKeys.EnterManuallyQuestion,
                                )}
                              </KioskButton>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Desktop: Regular Dropdown — aligned to search group */}
                  <div
                    className="hidden md:flex absolute z-10 top-full mt-1 bg-white border border-kioskPrimary rounded-2xl max-h-[300px] flex-col overflow-hidden"
                    data-testid="search-results-dropdown-desktop"
                    style={{
                      left: searchGroupRef.current?.offsetLeft ?? 0,
                      width: searchGroupRef.current?.offsetWidth ?? "100%",
                    }}
                  >
                    {isSearching && searchResults.length === 0 ? (
                      <div className="flex items-center justify-center p-4">
                        <Loader2 className="animate-spin" size={20} />
                      </div>
                    ) : searchResults?.length > 0 ? (
                      <>
                        {/* Scrollable results area */}
                        <div
                          ref={dropdownScrollRef}
                          className="overflow-y-auto scrollbar-hide"
                        >
                          {searchResults.map((result, index) => (
                            <button
                              key={`${result.ehrId}-${index}`}
                              type="button"
                              data-pharmacy-result
                              data-testid={`search-result-${index}`}
                              className="w-full px-6 py-4 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-all duration-200"
                              onClick={() => handleSelectResult(result)}
                            >
                              <span className="font-medium">
                                {highlightSearchMatch(
                                  result.name ?? "",
                                  searchValue,
                                )}
                              </span>{" "}
                              {result.streetAddress}, {result.city},{" "}
                              {result.state} {result.zip}
                            </button>
                          ))}
                          {/* Loading more indicator */}
                          {isSearching && searchResults.length > 0 && (
                            <div className="flex items-center justify-center p-2">
                              <Loader2 className="animate-spin" size={16} />
                              <span className="ml-2 text-sm text-gray-500">
                                {t_typeform(
                                  TypeformIntakeKeys.LoadingMoreResults,
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                        {/* Sticky footer with "Enter Manually" button */}
                        {(searchPage >= showManualAfterPages ||
                          !hasMoreResults) && (
                          <div className="border-t border-gray-200 p-4 bg-white rounded-b-2xl sticky bottom-0 flex items-center gap-1 justify-center">
                            <p className="text-sm text-gray-500 text-center">
                              {mode === "pcp"
                                ? t_typeform(
                                    TypeformIntakeKeys.NotSeeingYourPCP,
                                  )
                                : t_typeform(
                                    TypeformIntakeKeys.NotSeeingYourPharmacy,
                                  )}
                            </p>
                            <div className="flex flex-row gap-2 items-center justify-center">
                              <KioskButton
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="flex items-center px-3"
                                onClick={() => {
                                  setIsManualEntry(true);
                                  onContinueDisableChange?.(
                                    computeContinueDisabled(formData, true),
                                  );
                                }}
                                fullWidth={false}
                                data-testid="enter-manually-button"
                              >
                                <Pencil className="h-4 w-4 min-w-4" />
                                {t_typeform(TypeformIntakeKeys.EnterManually)}
                              </KioskButton>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      searchFinished && (
                        <div className="flex flex-col items-center justify-center p-4">
                          <p className="text-sm text-gray-500">
                            {t_typeform(TypeformIntakeKeys.NoResultsFound)}
                          </p>
                          <KioskButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="flex items-center"
                            onClick={() => {
                              setIsManualEntry(true);
                              onContinueDisableChange?.(
                                computeContinueDisabled(formData, true),
                              );
                            }}
                            fullWidth={false}
                            data-testid="enter-manually-button"
                          >
                            <Pencil className="h-4 w-4" />
                            {t_typeform(
                              TypeformIntakeKeys.EnterManuallyQuestion,
                            )}
                          </KioskButton>
                        </div>
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Switch back from manual entry to search */}
              <KioskButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsManualEntry(false);
                  setSearchValue("");
                  setSearchResults([]);
                  setIsSearching(false);
                  setShowDropdown(false);
                  setSearchPage(0);
                  setHasMoreResults(false);

                  // Switching back to search mode, recompute disabled state
                  onContinueDisableChange?.(
                    computeContinueDisabled(formData, false),
                  );
                }}
                data-testid="search-instead-button"
                leftIcon={<Search className="h-4 w-4" />}
              >
                {t_typeform(TypeformIntakeKeys.SearchInstead)}
              </KioskButton>
            </>
          )}
        </div>
      )}

      {/* Selected provider summary with remove option */}
      {hasSelectedValue ? (
        <div
          className="w-full flex flex-row items-start gap-4 relative border border-gray-200 rounded-3xl p-4"
          data-testid="selected-provider-summary"
        >
          <div className="flex-grow">
            <p className="font-medium" data-testid="selected-provider-name">
              {formData.name}
            </p>
            {formData.address.streetAddress && (
              <p className="text-sm text-gray-500">
                {formData.address.streetAddress}, {formData.address.city},{" "}
                {formData.address.state} {formData.address.zip}
              </p>
            )}
            {formData.phone && (
              <p className="text-sm text-gray-500">{formData.phone}</p>
            )}
          </div>
          <KioskButton
            type="button"
            variant="ghost"
            size="sm"
            fullWidth={false}
            onClick={handleReset}
            className="shrink-0 text-red-500 hover:text-red-600 rounded-full p-2"
            data-testid="reset-provider-button"
            leftIcon={<Trash2 className="h-4 w-4" />}
          />
        </div>
      ) : isManualEntry ? (
        <>
          {/* Manual entry form (name/address/phone) */}
          <div
            className="flex flex-col gap-4 w-full"
            data-testid="manual-entry-form"
          >
            <div className="flex flex-wrap gap-x-6 gap-y-4 w-full">
              {mode === "pharmacy" ? (
                <div className="w-full">
                  <KioskLabel htmlFor="name">
                    {t_account(AccountProfileKeys.Name)}
                  </KioskLabel>
                  <KioskNameInput
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleFormChange("name", e.target.value)}
                    placeholder=""
                    error={error && !formData.name}
                    data-testid="manual-name-input"
                  />
                </div>
              ) : (
                <div className="w-full">
                  <div className="flex gap-2">
                    <div className="flex-grow">
                      <KioskLabel htmlFor="firstName">
                        {t_account(AccountProfileKeys.FirstName)}
                      </KioskLabel>
                      <KioskNameInput
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) =>
                          handleFormChange("firstName", e.target.value)
                        }
                        placeholder=""
                        data-testid="manual-firstname-input"
                      />
                    </div>
                    <div className="flex-grow">
                      <KioskLabel htmlFor="lastName">
                        {t_account(AccountProfileKeys.LastName)}
                      </KioskLabel>
                      <KioskNameInput
                        value={formData.lastName}
                        onChange={(e) =>
                          handleFormChange("lastName", e.target.value)
                        }
                        placeholder=""
                        error={error && !formData.lastName}
                        data-testid="manual-lastname-input"
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="w-full">
                <KioskLabel htmlFor="phone">
                  {t_account(AccountProfileKeys.Phone)}
                </KioskLabel>
                <KioskInput
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleFormChange("phone", e.target.value)}
                  placeholder="(XXX) XXX-XXXX"
                  type="tel"
                  data-testid="manual-phone-input"
                />
              </div>
              <div className="w-full">
                <KioskLabel htmlFor="address">
                  {t_account(AccountProfileKeys.Address)}
                </KioskLabel>
                <div className="flex flex-col gap-2 w-full">
                  <KioskInput
                    id="address"
                    value={formData.address.streetAddress}
                    onChange={(e) =>
                      handleAddressChange("streetAddress", e.target.value)
                    }
                    placeholder=""
                  />
                  <div className="flex gap-2">
                    <div className="flex-grow">
                      <KioskLabel htmlFor="city">
                        {t_account(AccountProfileKeys.City)}
                      </KioskLabel>
                      <KioskInput
                        id="city"
                        value={formData.address.city}
                        onChange={(e) =>
                          handleAddressChange("city", e.target.value)
                        }
                        placeholder=""
                      />
                    </div>
                    <div className="flex-grow">
                      <KioskLabel htmlFor="state">
                        {t_account(AccountProfileKeys.State)}
                      </KioskLabel>
                      <KioskInput
                        id="state"
                        value={formData.address.state}
                        onChange={(e) =>
                          handleAddressChange("state", e.target.value)
                        }
                        placeholder=""
                      />
                    </div>
                  </div>
                  <div className="flex-grow">
                    <KioskLabel htmlFor="zip">
                      {t_account(AccountProfileKeys.Zip)}
                    </KioskLabel>
                    <KioskInput
                      id="zip"
                      maxLength={5}
                      value={formData.address.zip}
                      onChange={(e) => {
                        if (/[a-zA-Z]/.test(e.target.value)) {
                          return;
                        }
                        handleAddressChange("zip", e.target.value);
                      }}
                      placeholder=""
                      error={
                        error &&
                        (!formData.address.zip ||
                          formData.address.zip?.length !== 5)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};
